import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { useNavigate } from 'react-router-dom';
import {
  ParameterData,
  parameterStatusUtils,
  SimpleParameterStatus,
} from '../types/ParameterData';
import { ParametersTrendCard } from './ParametersTrendCard';

interface Props {
  data: ParameterData[];
}

const STATUS_CONFIGS: { status: SimpleParameterStatus; bgColor: string }[] = [
  { status: 'normal', bgColor: 'bg-parameter-normal' },
  { status: 'warning', bgColor: 'bg-parameter-warning' },
  { status: 'critical', bgColor: 'bg-parameter-critical' },
  { status: 'outdated', bgColor: 'bg-parameter-outdated' },
];

export const RiskHealthTrend = ({ data }: Props) => {
  const aggregatedData = parameterStatusUtils.aggregateParametersByName(data);

  const parametersByName = data.reduce((acc, param) => {
    if (!acc[param.name]) {
      acc[param.name] = [];
    }
    acc[param.name].push(param);
    return acc;
  }, {} as Record<string, ParameterData[]>);

  const statusOrder = {
    critical: 0,
    warning: 1,
    normal: 2,
    outdated: 3,
  };

  const statusData = STATUS_CONFIGS
  .map(({ status }) => ({
    status,
    data: parameterStatusUtils.filterDataByStatus(aggregatedData, status),
  }))
  .sort((a, b) => statusOrder[a.status] - statusOrder[b.status]);

  const navigate = useNavigate();

  return (
    <Accordion type='multiple'>
      {statusData.map(({ status, data }) => (
        <AccordionItem key={status} value={status} className='min-h-[78px]'>
          <AccordionTrigger className='h-[78px] flex gap-4 justify-between items-center'>
            <p className='text-xl font-semibold w-full'>
              {parameterStatusUtils.getGenericLabelForSimpleStatus(status)}
            </p>
            <div className='flex gap-2 items-center'>
              <p>{data.length}</p>
              <div
                className={`h-2 w-2 ${
                  STATUS_CONFIGS.find((c) => c.status === status)?.bgColor
                } rounded-full`}
              />
            </div>
          </AccordionTrigger>
          <AccordionContent className='space-y-sm'>
            {data.map((aggregatedParam) => (
              <ParametersTrendCard
                key={aggregatedParam.name}
                values={parametersByName[aggregatedParam.name]}
                onInfoClick={() =>
                  navigate(
                    `/parameter-info?parameter=${aggregatedParam.name}&&graph=true`,
                  )
                }
              />
            ))}
          </AccordionContent>
          <div className='w-full bg-border h-[1px]'/>
        </AccordionItem>
      ))}
    </Accordion>
  );
};
